import {runInAction} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {DataObjectId} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {MessageBox} from '@components/message-box'
import {TimelineNewStore} from '@components/timeline-new/timeline-new-store'
import {Point} from '@components/timeline-new/types'
import {
  CannotCreateLinkCycleError,
  Gantt,
  LinkCycleEncounteredError
} from '@extensions/gantt'

export class LinkHelper {
  private currentPoint: Point
  private isLinking: boolean

  private linkDrag: ReturnType<typeof requestAnimationFrame> | undefined

  constructor(private readonly store: TimelineNewStore) {
    this.startBoxLink = this.startBoxLink.bind(this)
    this.globalMouseMove = this.globalMouseMove.bind(this)
    this.globalMouseUp = this.globalMouseUp.bind(this)
    this.boxMouseUp = this.boxMouseUp.bind(this)
  }

  destroy(): void {
    if (this.linkDrag) {
      cancelAnimationFrame(this.linkDrag)
      this.linkDrag = undefined
    }
  }

  startBoxLink(
    path: string,
    hubId: DataObjectId,
    swimlaneIx: number,
    e: React.MouseEvent
  ): void {
    e.stopPropagation()

    if (!this.store.state.swimlanesEl || !this.store.state.tableScrollableEl) {
      return
    }

    const rect = this.store.state.swimlanesEl.getBoundingClientRect()
    const scrollTop = this.store.state.tableScrollableEl.scrollTop
    this.currentPoint = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top + scrollTop
    }
    this.isLinking = true
    this.subscribe()

    runInAction(() => {
      this.store.state.linkingPath = path
      this.store.state.linkingHubId = hubId
      this.store.state.linkingSwimlaneIx = swimlaneIx
      this.store.state.linkingPoint = this.currentPoint
      this.store.setCursorClass('!cursor-pointer')
    })
  }

  boxMouseUp(toHubId: DataObjectId, e: React.MouseEvent): void {
    e.stopPropagation()

    const fromHubId = this.store.state.linkingHubId
    if (!this.isLinking || !fromHubId || fromHubId === toHubId) {
      return
    }

    if (!this.store.canEditLinkBetween(fromHubId, toHubId)) {
      MessageBox.warn(Lang.msg.permissionsForbiddenTaskConnect)
      return
    }

    try {
      Gantt.linkTasks(fromHubId, toHubId)
    } catch (e) {
      if (e instanceof LinkCycleEncounteredError) {
        MessageBox.warn(Lang.msg.timeline.errorLinkCycleEncountered)
      } else if (e instanceof CannotCreateLinkCycleError) {
        MessageBox.warn(Lang.msg.timeline.errorCannotCreateLinkCycle)
      }
    }

    this.unsubscribe()
    this.isLinking = false

    runInAction(() => {
      this.store.state.linkingPath = undefined
      this.store.state.linkingHubId = undefined
      this.store.setCursorClass('')
      this.store.calculateTableRows()
    })

    void Tracking.send(TrackingEvent.ganttCreateDependencyViaGanttChart)
  }

  private globalMouseMove(e: MouseEvent): void {
    if (
      !this.isLinking ||
      !this.store.state.swimlanesEl ||
      !this.store.state.tableScrollableEl
    ) {
      return
    }

    const rect = this.store.state.swimlanesEl.getBoundingClientRect()
    const scrollTop = this.store.state.tableScrollableEl.scrollTop
    this.currentPoint = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top + scrollTop
    }

    runInAction(() => {
      this.store.state.linkingPoint = this.currentPoint
    })
  }

  private globalMouseUp(): void {
    if (!this.isLinking) {
      return
    }

    setTimeout(() => {
      if (!this.isLinking) {
        return
      }

      this.unsubscribe()
      this.isLinking = false

      runInAction(() => {
        this.store.state.linkingPath = undefined
        this.store.state.linkingHubId = undefined
        this.store.state.linkingPoint = {x: 0, y: 0}
        this.store.setCursorClass('')
      })
    })
  }

  private subscribe(): void {
    document.addEventListener('mousemove', this.globalMouseMove, {
      capture: true
    })
    document.addEventListener('mouseup', this.globalMouseUp, {capture: true})
  }

  private unsubscribe(): void {
    document.removeEventListener('mousemove', this.globalMouseMove, {
      capture: true
    })
    document.removeEventListener('mouseup', this.globalMouseUp, {capture: true})
  }
}
